import { Grid2 } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import MenuBar from "../../components/Menu/MenuBar";
import Sidebar from "../../components/Sidebar";
import { apiGetBrandById, apiGetBrandIndustries } from "../../services/brand.api";
import { apiGetEnrichmentJob } from "../../services/enrichment.api";
import { apiGetUserProfile } from "../../services/user.api";
import { useAsyncJobStore } from "../../store/useAsyncJobStore";
import { useAuthStore } from "../../store/useAuthStore";
import { palette } from "../../theme";
import { AsyncJobStatus } from "../../types/async-jobs";
import { useMessagesStore } from "../../store/useMessagesStore";

const POLL_MS = 3_000;

export const AppLayout = () => {
  const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(true);
  const { user, userProfile, setUserProfile, setUserAdminBrand } = useAuthStore();
  const { asyncJobs, updateAsyncJob } = useAsyncJobStore();
  const { addSuccess } = useMessagesStore();

  // Load user profile on app startup (needed for sidebar brand hierarchy)
  useEffect(() => {
    const loadProfile = async () => {
      if (user && !userProfile) {
        const profile = await apiGetUserProfile();
        if (profile) {
          setUserProfile(profile);

          // Fetch admin brand if user is brand admin
          if (profile.admin?.brand_id) {
            const brand = await apiGetBrandById(profile.admin.brand_id);
            if (brand) {
              setUserAdminBrand(brand);
            }
          }
        }
      }
    };
    loadProfile();
  }, [user, userProfile, setUserProfile, setUserAdminBrand]);

  // Fetch industries on app load
  useEffect(() => {
    apiGetBrandIndustries();
  }, []);

  // Derive a stable key from active job IDs so intervals only reset when the set of active jobs changes
  const activeJobIds = useMemo(() => {
    return asyncJobs
      .filter((j) => j.status === AsyncJobStatus.PENDING || j.status === AsyncJobStatus.PROCESSING)
      .map((j) => j.id)
      .sort()
      .join(",");
  }, [asyncJobs]);

  const intervalsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  useEffect(() => {
    const ids = activeJobIds ? activeJobIds.split(",") : [];
    const currentIntervals = intervalsRef.current;

    // Clear intervals for jobs no longer active
    currentIntervals.forEach((interval, id) => {
      if (!ids.includes(id)) {
        clearInterval(interval);
        currentIntervals.delete(id);
      }
    });

    // Start intervals for new active jobs
    ids.forEach((id) => {
      if (!id || currentIntervals.has(id)) return;
      const interval = setInterval(async () => {
        const res = await apiGetEnrichmentJob(id);
        if (!res) return;
        updateAsyncJob(res.id, res);
        if ([AsyncJobStatus.COMPLETED, AsyncJobStatus.FAILED].includes(res.status)) {
          clearInterval(interval);
          currentIntervals.delete(id);
          addSuccess(`Enrichment job ${res.id} has been completed with status: ${res.status === AsyncJobStatus.COMPLETED ? "Success" : "Failed"}`);
        }
      }, POLL_MS);
      currentIntervals.set(id, interval);
    });

    return () => {
      currentIntervals.forEach((interval) => clearInterval(interval));
      currentIntervals.clear();
    };
  }, [activeJobIds, updateAsyncJob])

  return (
    <Grid2 sx={{ display: "flex", minHeight: "100dvh", bgcolor: palette.background.light }}>
      <Sidebar
        expanded={sidebarExpanded}
        onToggle={() => setSidebarExpanded(!sidebarExpanded)}
      />
      <Grid2
        sx={{
          flex: 1,
          marginLeft: { lg: sidebarExpanded ? "280px" : "96px", md: 0 },
          transition: "margin-left 0.2s ease-in-out",
          minHeight: "100dvh",
          bgcolor: "#F5F7FA",
        }}
      >
        <MenuBar />
        <Grid2
          width="100%"
          container
          display="flex"
          alignContent="center"
          justifyContent="center"
        >
          <Grid2 maxWidth="1440px" width="100%" padding="32px">
            <Outlet />
          </Grid2>
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

export default AppLayout;
